import type { DiveDataSource } from "../dive/source.ts";
import { api } from "./client.ts";
import { isApiRequestError } from "./errors.ts";

/** 409 "dive set rebuilt" on a tile or overview: refetch the manifest and start over. */
export const isStale = (error: unknown): boolean =>
  isApiRequestError(error) && error.code === "conflict" && error.status === 409;

/** The live (or fixtures-mode) data source of the dive view, over the typed API client. */
export const diveSource: DiveDataSource = {
  manifest: (lakeSlug, signal) => api.diveManifest(lakeSlug, signal),
  async tile(lakeSlug, x, y, version, signal) {
    try {
      return await api.diveTile(lakeSlug, x, y, version, signal);
    } catch (e) {
      if (isApiRequestError(e) && e.code === "not_found") return null;
      throw e;
    }
  },
  overview: async (lakeSlug, version, signal) => api.diveOverview(lakeSlug, version, signal),
  async spots(lakeSlug, signal) {
    const { spots } = await api.listSpots({ lake: lakeSlug, status: "published" }, signal);
    // a spot unpublished or deleted between the list and its detail is simply gone
    const details = await Promise.all(
      spots.map((s) =>
        api.getSpot(s.slug, signal).catch((e: unknown) => {
          if (isApiRequestError(e) && e.code === "not_found") return null;
          throw e;
        }),
      ),
    );
    return details.filter((s) => s !== null);
  },
  isStale,
};
